// frontend/src/modules/admin/pages/GerenciarLeads.jsx
import React, { useState, useEffect } from 'react';
import { 
  FaCheck, FaTimes, FaEye, FaBuilding, FaTrash, 
  FaSearch, FaFilter, FaWhatsapp, FaEnvelope, FaPhone 
} from 'react-icons/fa';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

function GerenciarLeads() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtroTipo, setFiltroTipo] = useState('');
  const [filtroStatus, setFiltroStatus] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [leadSelecionado, setLeadSelecionado] = useState(null);
  const [estatisticas, setEstatisticas] = useState(null);

  const token = localStorage.getItem('token');

  // ============================================
  // BUSCAR LEADS
  // ============================================
  const fetchLeads = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filtroTipo) params.append('tipo', filtroTipo);
      if (filtroStatus) params.append('status', filtroStatus);
      if (searchTerm) params.append('search', searchTerm);

      const response = await fetch(`${API_URL}/api/leads?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await response.json();
      setLeads(data.leads || []);
    } catch (err) {
      console.error('Erro:', err);
    } finally {
      setLoading(false);
    }
  };

  // ============================================
  // BUSCAR ESTATÍSTICAS
  // ============================================
  const fetchEstatisticas = async () => {
    try {
      const response = await fetch(`${API_URL}/api/leads/admin/estatisticas`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setEstatisticas(data.estatisticas);
      }
    } catch (err) {
      console.error('Erro:', err);
    }
  };

  useEffect(() => {
    fetchLeads();
    fetchEstatisticas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtroTipo, filtroStatus]);

  // ============================================
  // ATUALIZAR STATUS
  // ============================================
  const handleAtualizarStatus = async (id, status) => {
    try {
      await fetch(`${API_URL}/api/leads/${id}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ status })
      });
      fetchLeads();
      fetchEstatisticas();
    } catch (err) {
      console.error(err);
    }
  };

  // ============================================
  // CONVERTER EM EMPRESA
  // ============================================
  const handleConverter = async (id) => {
    if (!window.confirm('Converter este lead em empresa?')) return;

    try {
      const response = await fetch(`${API_URL}/api/leads/${id}/converter`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.error);

      alert('✅ Lead convertido em empresa!');
      fetchLeads();
      fetchEstatisticas();
    } catch (err) {
      alert('❌ ' + err.message);
    }
  };

  // ============================================
  // DELETAR LEAD
  // ============================================
  const handleExcluir = async (id) => {
    if (!window.confirm('Excluir este lead?')) return;

    try {
      await fetch(`${API_URL}/api/leads/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchLeads();
      fetchEstatisticas();
    } catch (err) {
      alert('❌ ' + err.message);
    }
  };

  // ============================================
  // CORES
  // ============================================
  const getStatusColor = (status) => {
    const colors = {
      'NOVO': { bg: '#cce5ff', color: '#004085' },
      'CONTATADO': { bg: '#fff3cd', color: '#856404' },
      'QUALIFICADO': { bg: '#d1ecf1', color: '#0c5460' },
      'CONVERTIDO': { bg: '#d4edda', color: '#155724' },
      'PERDIDO': { bg: '#f8d7da', color: '#721c24' }
    };
    return colors[status] || { bg: '#e9ecef', color: '#6c757d' };
  };

  return (
    <div style={{ padding: '20px' }}>
      <h2>📥 Leads Recebidos</h2>

      {/* Estatísticas */}
      {estatisticas && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
          gap: '15px',
          marginBottom: '20px'
        }}>
          <div style={{ background: '#cce5ff', padding: '15px', borderRadius: '8px', textAlign: 'center' }}>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#004085' }}>
              {estatisticas.total}
            </div>
            <div style={{ fontSize: '12px', color: '#004085' }}>Total</div>
          </div>
          <div style={{ background: '#fff3cd', padding: '15px', borderRadius: '8px', textAlign: 'center' }}>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#856404' }}>
              {estatisticas.porStatus?.novos || 0}
            </div>
            <div style={{ fontSize: '12px', color: '#856404' }}>Novos</div>
          </div>
          <div style={{ background: '#d1ecf1', padding: '15px', borderRadius: '8px', textAlign: 'center' }}>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#0c5460' }}>
              {estatisticas.porStatus?.qualificados || 0}
            </div>
            <div style={{ fontSize: '12px', color: '#0c5460' }}>Qualificados</div>
          </div>
          <div style={{ background: '#d4edda', padding: '15px', borderRadius: '8px', textAlign: 'center' }}>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#155724' }}>
              {estatisticas.porStatus?.convertidos || 0}
            </div>
            <div style={{ fontSize: '12px', color: '#155724' }}>Convertidos</div>
          </div>
          <div style={{ background: '#e9ecef', padding: '15px', borderRadius: '8px', textAlign: 'center' }}>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#495057' }}>
              {estatisticas.ultimos7dias || 0}
            </div>
            <div style={{ fontSize: '12px', color: '#495057' }}>Últimos 7 dias</div>
          </div>
        </div>
      )}

      {/* Filtros */}
      <div style={{
        display: 'flex',
        gap: '10px',
        marginBottom: '20px',
        flexWrap: 'wrap'
      }}>
        <div style={{ flex: 1, minWidth: '200px', position: 'relative' }}>
          <FaSearch style={{
            position: 'absolute',
            left: '12px',
            top: '50%',
            transform: 'translateY(-50%)',
            color: '#adb5bd'
          }} />
          <input
            type="text"
            placeholder="Buscar por nome, email, CNPJ..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && fetchLeads()}
            style={{
              width: '100%',
              padding: '10px 15px 10px 40px',
              border: '1px solid #ddd',
              borderRadius: '6px',
              fontSize: '14px',
              boxSizing: 'border-box'
            }}
          />
        </div>

        <select
          value={filtroTipo}
          onChange={(e) => setFiltroTipo(e.target.value)}
          style={{
            padding: '10px 15px',
            border: '1px solid #ddd',
            borderRadius: '6px',
            fontSize: '14px',
            minWidth: '150px'
          }}
        >
          <option value="">Todos os tipos</option>
          <option value="ABERTURA">🏢 Abertura</option>
          <option value="ENCERRAMENTO">🚪 Encerramento</option>
        </select>

        <select
          value={filtroStatus}
          onChange={(e) => setFiltroStatus(e.target.value)}
          style={{
            padding: '10px 15px',
            border: '1px solid #ddd',
            borderRadius: '6px',
            fontSize: '14px',
            minWidth: '150px'
          }}
        >
          <option value="">Todos os status</option>
          <option value="NOVO">🆕 Novo</option>
          <option value="CONTATADO">📞 Contatado</option>
          <option value="QUALIFICADO">⭐ Qualificado</option>
          <option value="CONVERTIDO">✅ Convertido</option>
          <option value="PERDIDO">❌ Perdido</option>
        </select>

        <button
          onClick={fetchLeads}
          style={{
            padding: '10px 20px',
            background: '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '14px'
          }}
        >
          🔄 Atualizar
        </button>
      </div>

      {/* Lista */}
      {loading ? (
        <p style={{ textAlign: 'center', padding: '40px' }}>🔄 Carregando...</p>
      ) : leads.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px', color: '#6c757d' }}>
          <div style={{ fontSize: '48px', marginBottom: '15px' }}>📭</div>
          <p>Nenhum lead encontrado</p>
        </div>
      ) : (
        <div style={{
          background: 'white',
          borderRadius: '8px',
          overflow: 'hidden',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f8f9fa' }}>
                <th style={{ padding: '12px', textAlign: 'left' }}>Tipo</th>
                <th style={{ padding: '12px', textAlign: 'left' }}>Nome</th>
                <th style={{ padding: '12px', textAlign: 'left' }}>Contato</th>
                <th style={{ padding: '12px', textAlign: 'left' }}>Data</th>
                <th style={{ padding: '12px', textAlign: 'center' }}>Status</th>
                <th style={{ padding: '12px', textAlign: 'center' }}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {leads.map((lead) => {
                const statusColor = getStatusColor(lead.status);
                return (
                  <tr key={lead.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={{ padding: '12px' }}>
                      <span style={{
                        padding: '4px 10px',
                        borderRadius: '12px',
                        fontSize: '11px',
                        background: lead.tipo === 'ABERTURA' ? '#d4edda' : '#f8d7da',
                        color: lead.tipo === 'ABERTURA' ? '#155724' : '#721c24'
                      }}>
                        {lead.tipo === 'ABERTURA' ? '🏢 Abertura' : '🚪 Encerramento'}
                      </span>
                    </td>
                    <td style={{ padding: '12px' }}>
                      <div style={{ fontWeight: '600' }}>{lead.nome_completo}</div>
                      {lead.nome_empresa && (
                        <div style={{ fontSize: '12px', color: '#6c757d' }}>
                          🏢 {lead.nome_empresa}
                        </div>
                      )}
                    </td>
                    <td style={{ padding: '12px' }}>
                      <div style={{ fontSize: '13px' }}>✉️ {lead.email}</div>
                      {lead.telefone && (
                        <div style={{ fontSize: '12px', color: '#6c757d' }}>
                          📱 {lead.telefone}
                        </div>
                      )}
                    </td>
                    <td style={{ padding: '12px', fontSize: '13px', color: '#6c757d' }}>
                      {new Date(lead.criado_em).toLocaleDateString('pt-BR')}
                    </td>
                    <td style={{ padding: '12px', textAlign: 'center' }}>
                      <span style={{
                        padding: '4px 12px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: '600',
                        background: statusColor.bg,
                        color: statusColor.color
                      }}>
                        {lead.status}
                      </span>
                    </td>
                    <td style={{ padding: '12px', textAlign: 'center' }}>
                      <div style={{ display: 'flex', gap: '5px', justifyContent: 'center', flexWrap: 'wrap' }}>
                        <button
                          onClick={() => setLeadSelecionado(lead)}
                          title="Ver detalhes"
                          style={{ background: 'none', border: 'none', color: '#007bff', cursor: 'pointer', padding: '5px' }}
                        >
                          <FaEye />
                        </button>

                        {lead.telefone && (
                          <a
                            href={`https://wa.me/55${lead.telefone.replace(/\D/g, '')}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            title="WhatsApp"
                            style={{ color: '#25D366', padding: '5px' }}
                          >
                            <FaWhatsapp />
                          </a>
                        )}

                        {lead.status === 'NOVO' && (
                          <button
                            onClick={() => handleAtualizarStatus(lead.id, 'CONTATADO')}
                            title="Marcar como contatado"
                            style={{ background: 'none', border: 'none', color: '#ffc107', cursor: 'pointer', padding: '5px' }}
                          >
                            <FaCheck />
                          </button>
                        )}

                        {lead.tipo === 'ABERTURA' && lead.status !== 'CONVERTIDO' && (
                          <button
                            onClick={() => handleConverter(lead.id)}
                            title="Converter em empresa"
                            style={{ background: 'none', border: 'none', color: '#28a745', cursor: 'pointer', padding: '5px' }}
                          >
                            <FaBuilding />
                          </button>
                        )}

                        <button
                          onClick={() => handleExcluir(lead.id)}
                          title="Excluir"
                          style={{ background: 'none', border: 'none', color: '#dc3545', cursor: 'pointer', padding: '5px' }}
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal Detalhes */}
      {leadSelecionado && (
        <div
          style={{
            position: 'fixed',
            top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '20px'
          }}
          onClick={() => setLeadSelecionado(null)}
        >
          <div
            style={{
              background: 'white',
              padding: '30px',
              borderRadius: '12px',
              maxWidth: '600px',
              width: '100%',
              maxHeight: '90vh',
              overflowY: 'auto'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ margin: 0 }}>
                {leadSelecionado.tipo === 'ABERTURA' ? '🏢 Abertura' : '🚪 Encerramento'}
              </h3>
              <button
                onClick={() => setLeadSelecionado(null)}
                style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer' }}
              >
                <FaTimes />
              </button>
            </div>

            <div style={{ lineHeight: '1.8' }}>
              <p><strong>Nome:</strong> {leadSelecionado.nome_completo}</p>
              <p><strong>Email:</strong> {leadSelecionado.email}</p>
              {leadSelecionado.telefone && <p><strong>Telefone:</strong> {leadSelecionado.telefone}</p>}
              {leadSelecionado.cpf_cnpj && <p><strong>CPF/CNPJ:</strong> {leadSelecionado.cpf_cnpj}</p>}
              {leadSelecionado.nome_empresa && <p><strong>Empresa:</strong> {leadSelecionado.nome_empresa}</p>}
              {leadSelecionado.atividade && <p><strong>Atividade:</strong> {leadSelecionado.atividade}</p>}
              {leadSelecionado.regime_desejado && <p><strong>Regime:</strong> {leadSelecionado.regime_desejado}</p>}
              {leadSelecionado.cnpj_encerrar && <p><strong>CNPJ Encerrar:</strong> {leadSelecionado.cnpj_encerrar}</p>}
              {leadSelecionado.motivo_encerrar && <p><strong>Motivo:</strong> {leadSelecionado.motivo_encerrar}</p>}
              {leadSelecionado.mensagem && (
                <div style={{
                  background: '#f8f9fa',
                  padding: '15px',
                  borderRadius: '6px',
                  marginTop: '15px'
                }}>
                  <strong>Mensagem:</strong>
                  <p style={{ margin: '10px 0 0 0' }}>{leadSelecionado.mensagem}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default GerenciarLeads;